import {
	clientActionFromError,
	type SubscriptionStatus,
	verifyPurchase,
} from "@/entities/subscription"
import type { VerifyPurchaseRequest } from "@/shared/api/generated-backend"
import { assertSessionVersion, getSessionVersion } from "@/shared/lib/auth"
import { captureError } from "@/shared/lib/diagnostics"
import type { StoreId } from "@/shared/lib/platform"
import { purchaseErrorProps, wrapStep } from "./errors"

export type TransactionSource = "purchase" | "restore" | "unfinished"

/** Store-neutral transaction: the fields every provider maps its own payload onto. */
export type StoreTransaction = {
	id: string
	productId?: string
	verificationResult?: string
	token?: string
}

/** Closes the transaction with the store that issued it. Supplied by the provider. */
export type FinishTransaction = (transactionId: string) => Promise<void>

export type ProcessOutcome =
	| { status: "processed"; subscription: SubscriptionStatus }
	| { status: "failed"; error: unknown }

export type ProcessSummary = {
	failed: boolean
}

const inFlight = new Map<string, Promise<ProcessOutcome>>()

export function processTransaction(
	store: StoreId,
	transaction: StoreTransaction,
	source: TransactionSource,
	finish: FinishTransaction,
): Promise<ProcessOutcome> {
	const pending = inFlight.get(transaction.id)
	if (pending) return pending

	const run = runTransaction(store, transaction, source, finish).finally(() => {
		inFlight.delete(transaction.id)
	})
	inFlight.set(transaction.id, run)
	return run
}

export async function processTransactions(
	store: StoreId,
	transactions: StoreTransaction[],
	source: TransactionSource,
	finish: FinishTransaction,
): Promise<ProcessSummary> {
	const summary: ProcessSummary = { failed: false }
	const version = getSessionVersion()

	for (const transaction of transactions) {
		assertSessionVersion(version)
		const outcome = await processTransaction(store, transaction, source, finish)
		if (outcome.status === "failed") {
			summary.failed = true
			console.error(
				`Failed to process ${source} transaction:`,
				transaction.id,
				outcome.error,
			)
			captureError(outcome.error, {
				scope: `purchase.${source}`,
				store,
				transaction_id: transaction.id,
				product_id: transaction.productId,
				...purchaseErrorProps(outcome.error),
			})
		}
	}

	return summary
}

async function finishQuietly(
	store: StoreId,
	transactionId: string,
	finish: FinishTransaction,
) {
	try {
		await finish(transactionId)
	} catch (error) {
		captureError(error, {
			scope: "purchase.finish",
			store,
			transaction_id: transactionId,
			...purchaseErrorProps(error),
		})
	}
}

async function runTransaction(
	store: StoreId,
	transaction: StoreTransaction,
	source: TransactionSource,
	finish: FinishTransaction,
): Promise<ProcessOutcome> {
	const body = toVerifyRequest(store, transaction)
	if (!body) {
		return {
			status: "failed",
			error: new Error(
				`Store returned transaction ${transaction.id} without verification data`,
			),
		}
	}

	try {
		const { subscription, clientAction } = await wrapStep(
			`${store}/${source}.verifyPurchase`,
			() => verifyPurchase(body),
		)
		if (clientAction === "finish")
			await finishQuietly(store, transaction.id, finish)
		return { status: "processed", subscription }
	} catch (error) {
		if (clientActionFromError(error) === "finish") {
			await finishQuietly(store, transaction.id, finish)
		}
		return { status: "failed", error }
	}
}

function toVerifyRequest(
	store: StoreId,
	transaction: StoreTransaction,
): VerifyPurchaseRequest | null {
	if (!transaction.productId) return null

	const platformData = toPlatformData(store, transaction)
	if (!platformData) return null

	return {
		product_id: transaction.productId,
		transaction_id: transaction.id,
		platform_data: platformData,
	}
}

function toPlatformData(
	store: StoreId,
	transaction: StoreTransaction,
): Record<string, string> | null {
	switch (store) {
		case "app-store":
			if (!transaction.verificationResult) return null
			return { verification_result: transaction.verificationResult }
		case "google-play":
			if (!transaction.token) return null
			return { token: transaction.token }
		case "rustore":
			return { purchase_id: transaction.id }
	}
}
